"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  buildThemeStatements,
  checkTheme,
  EMPTY_THEME,
  flagCount,
  THEME_FRAMES,
  THEME_MODALS,
  type ThemeCheckId,
  type ThemeInput,
} from "@/lib/tool-kit/lit-essays/theme-statement";

/** English strings. A translated content file overrides these through `ui`. */
export const THEME_STATEMENT_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear",
  textHeading: "The text",
  titleLabel: "Title of the text",
  titlePlaceholder: "e.g. The Tell-Tale Heart",
  authorLabel: "Author",
  authorPlaceholder: "e.g. Edgar Allan Poe",
  charactersLabel: "Characters and other names (optional)",
  charactersPlaceholder: "e.g. the narrator, the old man",
  charactersHint:
    "Used by the check that a theme does not name characters or the title.",
  ideaHeading: "The idea",
  topicLabel: "Topic — the abstract idea, in a word or two",
  topicPlaceholder: "e.g. guilt",
  topicPicks: "Common topics",
  topics: [
    "ambition",
    "identity",
    "loss",
    "power",
    "belonging",
    "courage",
    "freedom",
    "justice",
    "memory",
    "family",
    "change",
    "fear",
    "guilt",
    "love",
  ],
  predicateLabel: "What the text says about it — start with a verb",
  predicatePlaceholder:
    "e.g. turn a hidden crime into the criminal’s own punishment",
  predicateHint:
    "Finish the sentence “[Topic] can …”. Write about people in general, not about this text’s characters.",
  circumstanceLabel: "When does that happen? (optional)",
  circumstancePlaceholder: "e.g. people try to bury what they have done",
  circumstanceHint:
    "A clause with its own subject. Builds the “When …” statement.",
  commonViewLabel: "What do people usually assume? (optional)",
  commonViewPlaceholder: "e.g. conscience seems easy to silence",
  commonViewHint: "A clause. Builds the “Although …” statement.",
  modalLabel: "Strength of the claim",
  modals: { can: "can", may: "may", tends: "tends to" },
  statementsHeading: "Theme statements",
  statementsIntro:
    "Each statement is built from your words with a fixed frame — honest templates, no AI. Rewrite one so it says exactly what your text suggests.",
  needIdea: "Enter a topic and what the text says about it to see statements.",
  statementLabels: ["Direct", "With a condition", "With a contrast"],
  flagsNone: "Passes every check",
  flagsSome: "one:{count} check to look at|other:{count} checks to look at",
  checkThis: "Check this one",
  copy: "Copy",
  copied: "Copied!",
  checkerHeading: "Check a theme statement",
  checkerIntro:
    "Paste a statement of your own, or load one from above. The checks flag common slips; they cannot judge whether the statement is true of your text.",
  draftLabel: "Theme statement to check",
  draftPlaceholder:
    "e.g. Guilt can turn a hidden crime into the criminal’s own punishment.",
  checkerEmpty: "Enter a statement to run the checks.",
  pass: "Pass",
  lookAgain: "Look again",
  checks: {
    length: {
      pass: "Long enough to be a statement.",
      flag: "Too short. A theme is a full sentence about the topic, not the topic itself.",
    },
    names: {
      pass: "Names no characters, author, or title.",
      flag: "Names a character, the author, or the title. A theme should hold for any text.",
    },
    command: {
      pass: "States an idea rather than giving an order.",
      flag: "Reads like a moral or an instruction (you, should, never, don’t). Say what the text suggests about people.",
    },
    absolute: {
      pass: "Avoids absolutes.",
      flag: "Uses an absolute such as always, never, or everyone. One counterexample defeats it; try can, may, or often.",
    },
    summary: {
      pass: "Reads as an idea, not a plot summary.",
      flag: "Sounds like a plot summary or refers to the text. Generalize to people and ideas.",
    },
    cliche: {
      pass: "Not a stock phrase.",
      flag: "Matches a stock phrase. Say what your text adds to it.",
    },
    topic: {
      pass: "Mentions the topic.",
      flag: "Does not mention your topic word. Is it about the idea you meant?",
    },
  },
  frames: THEME_FRAMES,
  example: {
    title: "The Tell-Tale Heart",
    author: "Edgar Allan Poe",
    characters: "the narrator, the old man",
    topic: "guilt",
    predicate: "turn a hidden crime into the criminal’s own punishment",
    circumstance: "people try to bury what they have done",
    commonView: "conscience seems easy to silence",
    modal: "can",
  },
} as const;

type Strings = typeof THEME_STATEMENT_GENERATOR_STRINGS;
type TextKey = Exclude<keyof ThemeInput, "modal">;

const BADGE_OK =
  "inline-flex w-fit rounded-md bg-primary/10 px-2 py-0.5 font-medium text-primary text-xs";
const BADGE_FLAG =
  "inline-flex w-fit rounded-md border border-border bg-muted px-2 py-0.5 font-medium text-foreground text-xs";

function CheckList({
  statement,
  input,
  strings,
}: {
  statement: string;
  input: ThemeInput;
  strings: Strings;
}) {
  const results = checkTheme(statement, input);
  return (
    <ul className="space-y-2">
      {results.map((result) => {
        const copy = strings.checks[result.id as ThemeCheckId];
        return (
          <li
            className="grid gap-1 sm:grid-cols-[6.5rem_1fr] sm:gap-3"
            key={result.id}
          >
            <span className={result.ok ? BADGE_OK : BADGE_FLAG}>
              {result.ok ? strings.pass : strings.lookAgain}
            </span>
            <span className="text-foreground text-sm leading-relaxed">
              {result.ok ? copy.pass : copy.flag}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export default function ThemeStatementGeneratorWidget() {
  const strings = useToolUi(THEME_STATEMENT_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [input, setInput] = useState<ThemeInput>(EMPTY_THEME);
  const [draft, setDraft] = useState("");
  const statements = buildThemeStatements(input, {
    modals: strings.modals,
    frames: strings.frames,
  });
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const field = (
    key: TextKey,
    label: string,
    placeholder: string,
    hint?: string
  ) => (
    <TextField
      hint={hint}
      id={`ts-${key}`}
      label={label}
      onChange={update(key)}
      placeholder={placeholder}
      value={input[key]}
    />
  );

  return (
    <>
      <div className={`${CARD} space-y-5`}>
        <div className="grid gap-4 sm:grid-cols-2">
          {field("title", strings.titleLabel, strings.titlePlaceholder)}
          {field("author", strings.authorLabel, strings.authorPlaceholder)}
        </div>
        {field(
          "characters",
          strings.charactersLabel,
          strings.charactersPlaceholder,
          strings.charactersHint
        )}
        <div className="space-y-2">
          {field("topic", strings.topicLabel, strings.topicPlaceholder)}
          <div className="flex flex-wrap items-center gap-2">
            <span className={HINT}>{strings.topicPicks}</span>
            {strings.topics.map((topic) => (
              <button
                className={BUTTON_SMALL}
                key={topic}
                onClick={() => update("topic")(topic)}
                type="button"
              >
                {topic}
              </button>
            ))}
          </div>
        </div>
        <TextAreaField
          hint={strings.predicateHint}
          id="ts-predicate"
          label={strings.predicateLabel}
          onChange={update("predicate")}
          placeholder={strings.predicatePlaceholder}
          prose
          rows={2}
          value={input.predicate}
        />
        <PillGroup
          label={strings.modalLabel}
          name="ts-modal"
          onChange={(modal) => setInput((previous) => ({ ...previous, modal }))}
          options={THEME_MODALS.map((value) => ({
            value,
            label: strings.modals[value],
          }))}
          value={input.modal}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          {field(
            "circumstance",
            strings.circumstanceLabel,
            strings.circumstancePlaceholder,
            strings.circumstanceHint
          )}
          {field(
            "commonView",
            strings.commonViewLabel,
            strings.commonViewPlaceholder,
            strings.commonViewHint
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setInput({ ...strings.example });
              setDraft("");
            }}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setInput(EMPTY_THEME);
              setDraft("");
            }}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </div>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.statementsHeading}</h2>
        {statements.length === 0 ? (
          <Notice>{strings.needIdea}</Notice>
        ) : (
          <>
            <p className="text-muted-foreground text-sm">
              {strings.statementsIntro}
            </p>
            {statements.map((statement, index) => {
              const flags = flagCount(checkTheme(statement, input));
              return (
                <div className={`${RESULT_CARD} space-y-2`} key={statement}>
                  <h3 className={WIDGET_H3}>
                    {strings.statementLabels[index]}
                  </h3>
                  <p className="text-foreground text-sm leading-relaxed">
                    {statement}
                  </p>
                  <p className={HINT}>
                    {flags === 0
                      ? strings.flagsNone
                      : tPlural(strings.flagsSome, locale, flags, {
                          count: fmt(flags),
                        })}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      className={BUTTON_SMALL}
                      onClick={() => setDraft(statement)}
                      type="button"
                    >
                      {strings.checkThis}
                    </button>
                    <CopyButton
                      copiedLabel={strings.copied}
                      label={strings.copy}
                      small
                      text={statement}
                    />
                  </div>
                </div>
              );
            })}
          </>
        )}
      </section>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.checkerHeading}</h2>
        <p className="text-muted-foreground text-sm">{strings.checkerIntro}</p>
        <TextAreaField
          id="ts-draft"
          label={strings.draftLabel}
          onChange={setDraft}
          placeholder={strings.draftPlaceholder}
          prose
          rows={2}
          value={draft}
        />
        {draft.trim() === "" ? (
          <Notice>{strings.checkerEmpty}</Notice>
        ) : (
          <CheckList input={input} statement={draft} strings={strings} />
        )}
      </section>
    </>
  );
}
